import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageContainer from "../components/layout/PageContainer";

// Import all step components
import BuildingTypeSelector from "../components/products/configurator/BuildingTypeSelector";
import FloorsSelector from "../components/products/configurator/FloorsSelector";
import LiftTypeSelector from "../components/products/configurator/LiftTypeSelector";
import ShaftSizeSelector from "../components/products/configurator/ShaftSizeSelector";
import RequirementsSelector from "../components/products/configurator/RequirementsSelector";
import DoorTypeSelector from "../components/products/configurator/DoorTypeSelector";
import DesignFinishSelector from "../components/products/configurator/DesignFinishSelector";
import ConfigSummary from "../components/products/configurator/ConfigSummary";

const Products = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [configuration, setConfiguration] = useState({
    buildingType: "",
    floors: 2,
    liftType: "",
    shaftSize: { width: 1500, depth: 1500 },
    requirements: [],
    doorType: "",
    designFinish: "",
  });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Track progress through the configurator
  const [progress, setProgress] = useState(0);
  const totalSteps = 8; // Total number of configuration steps

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update the progress bar based on current step
  useEffect(() => {
    setProgress((currentStep / totalSteps) * 100);
  }, [currentStep]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isSmallMobile = windowWidth <= 480;

  // Update configuration based on step changes
  const updateConfiguration = (key, value) => {
    setConfiguration((prev) => ({ ...prev, [key]: value }));
  };

  // Move to next step after selection
  const goToNextStep = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    setCurrentStep((current) => current + 1);
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    setCurrentStep((current) => Math.max(0, current - 1));
  };

  // Jump to specific step (for edit from summary)
  const goToStep = (step) => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    setCurrentStep(step);
  };

  // Reset configuration and start over
  const resetConfiguration = () => {
    setConfiguration({
      buildingType: "",
      floors: 2,
      liftType: "",
      shaftSize: { width: 1500, depth: 1500 },
      requirements: [],
      doorType: "",
      designFinish: "",
    });
    setCurrentStep(0);
  };

  const steps = [
    {
      component: BuildingTypeSelector,
      title: "Building Type",
      subtitle: "Select the type of building for your elevator",
      icon: "building",
      props: {
        selectedType: configuration.buildingType,
        onSelect: (type) => updateConfiguration("buildingType", type),
        onContinue: goToNextStep,
      },
    },
    {
      component: FloorsSelector,
      title: "Floors",
      subtitle: "How many floors will the elevator serve?",
      icon: "layer-group",
      props: {
        floors: configuration.floors,
        buildingType: configuration.buildingType,
        onChange: (floors) => updateConfiguration("floors", floors),
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: LiftTypeSelector,
      title: "Lift Type",
      subtitle: "Choose the ideal lift technology for your needs",
      icon: "elevator",
      props: {
        selectedType: configuration.liftType,
        buildingType: configuration.buildingType,
        floors: configuration.floors,
        onSelect: (type) => updateConfiguration("liftType", type),
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: ShaftSizeSelector,
      title: "Shaft Size",
      subtitle: "Configure the dimensions of your elevator shaft",
      icon: "ruler-combined",
      props: {
        // Pass initialDimensions to properly initialize the component
        initialDimensions: configuration.shaftSize || {
          width: 1800,
          depth: 1900,
          height: 3000,
        },
        // This is the existing shaft size
        shaftSize: configuration.shaftSize,
        liftType: configuration.liftType,
        buildingType: configuration.buildingType,
        floors: configuration.floors,
        // Make sure we're handling the onChange correctly
        onChange: (size) => updateConfiguration("shaftSize", size),
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: RequirementsSelector,
      title: "Requirements",
      subtitle: "Select additional features and requirements",
      icon: "clipboard-list",
      props: {
        selectedRequirements: configuration.requirements, // This is already correct
        liftType: configuration.liftType,
        buildingType: configuration.buildingType, // You might want to add this
        floors: configuration.floors, // You might want to add this
        onChange: (reqs) => updateConfiguration("requirements", reqs),
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: DoorTypeSelector,
      title: "Door Style",
      subtitle: "Select the perfect door type for your elevator",
      icon: "door-open",
      props: {
        initialSelection: {
          doorType: Array.isArray(configuration.doorType)
            ? configuration.doorType
                .find((item) => item.startsWith("door_type_"))
                ?.split("_")[2] || "center"
            : "center",
          doorFinish: Array.isArray(configuration.doorType)
            ? configuration.doorType
                .find((item) => item.startsWith("door_finish_"))
                ?.split("_")[2] || "stainless"
            : "stainless",
        },
        onSelectionChange: (doorSelection) =>
          updateConfiguration("doorType", doorSelection),
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: DesignFinishSelector,
      title: "Design & Finish",
      subtitle: "Choose premium finishes and materials",
      icon: "paint-brush",
      props: {
        selectedFinish: configuration.designFinish || {},
        onSelectionChange: (designData) => {
          console.log("Design data received:", designData); // For debugging
          updateConfiguration("designFinish", designData);
        },
        onContinue: goToNextStep,
        onBack: goToPreviousStep,
      },
    },
    {
      component: ConfigSummary,
      title: "Review & Submit",
      subtitle: "Confirm your custom elevator configuration",
      icon: "check-circle",
      props: {
        configData: {
          // Building information
          buildingInfo: {
            buildingType: configuration.buildingType || "commercial",
            floors: configuration.floors || 2,
            liftType: configuration.liftType || "traction",
          },

          // Dimensions
          dimensions: {
            capacity: configuration.requirements?.find((req) =>
              req.startsWith("capacity_")
            )
              ? configuration.requirements
                  .find((req) => req.startsWith("capacity_"))
                  .split("_")[1] + "kg"
              : "1000kg",
            width: configuration.shaftSize?.width
              ? `${configuration.shaftSize.width}mm`
              : "1100mm",
            depth: configuration.shaftSize?.depth
              ? `${configuration.shaftSize.depth}mm`
              : "1400mm",
            height: configuration.shaftSize?.height
              ? `${configuration.shaftSize.height}mm`
              : "2200mm",
            speed: configuration.requirements?.find((req) =>
              req.startsWith("speed_")
            )
              ? configuration.requirements
                  .find((req) => req.startsWith("speed_"))
                  .split("_")[1] + "m/s"
              : "1.0m/s",
          },

          // Door configuration
          doorConfig: {
            doorType: Array.isArray(configuration.doorType)
              ? configuration.doorType
                  .find((item) => item.startsWith("door_type_"))
                  ?.split("_")[2] || "center"
              : configuration.doorType || "center",
            doorFinish: Array.isArray(configuration.doorType)
              ? configuration.doorType
                  .find((item) => item.startsWith("door_finish_"))
                  ?.split("_")[2] || "stainless"
              : typeof configuration.designFinish === "object"
              ? configuration.designFinish?.door || "stainless"
              : "stainless",
          },

          // Interior design
          interiorDesign: {
            wallFinish:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.wallFinish || "stainless"
                : "stainless",
            floorType:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.floorType || "granite"
                : "granite",
            handrailType:
              typeof configuration.designFinish === "object" &&
              configuration.designFinish.handrailType
                ? configuration.designFinish.handrailType
                : configuration.requirements?.includes("handrail")
                ? "round"
                : "standard",
            handrailFinish:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.handrailFinish || "stainless"
                : "stainless",
            lighting:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.lighting || "led-spots"
                : "led-spots",
            controlPanel:
              typeof configuration.designFinish === "object" &&
              configuration.designFinish.controlPanel
                ? configuration.designFinish.controlPanel
                : configuration.requirements?.includes("touchscreen")
                ? "touchscreen"
                : "buttons",
            wallLayout:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.wallLayout || "standard"
                : "standard",
            floorPattern:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.floorPattern || "standard"
                : "standard",
            mirror:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.mirror || false
                : false,
            display:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.display || false
                : false,
            ventilation:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.ventilation || false
                : false,
            kickPlates:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.kickPlates || false
                : false,
            controlFinish:
              typeof configuration.designFinish === "object"
                ? configuration.designFinish.controlFinish || "standard"
                : "standard",
          },

          // Safety features
          safety: {
            emergencyPower:
              configuration.requirements?.includes("emergencyPower") || false,
            fireService:
              configuration.requirements?.includes("fireAlarm") || false,
            intercom:
              configuration.requirements?.includes("emergencyCall") || false,
            camera:
              configuration.requirements?.includes("securityCamera") || false,
            overloadDetection:
              configuration.requirements?.includes("overload") || false,
          },

          // Accessibility features
          accessibility: {
            brailleButtons:
              configuration.requirements?.includes("braille") || false,
            voiceAnnouncement:
              configuration.requirements?.includes("voiceAnnouncements") ||
              false,
            mirror:
              configuration.requirements?.includes("visibilityMirror") || false,
            loweredButtons:
              configuration.requirements?.includes("loweredButtons") || false,
            handrails:
              configuration.requirements?.includes("handrails") || false,
          },
        },
        onBack: goToPreviousStep,
        onFinish: () => {
          console.log("Configuration complete", configuration);
          resetConfiguration();
          goToStep(0); // Go back to first step
        },
      },
    },
  ];

  // Get current step data
  const currentStepData = steps[currentStep];
  const StepComponent = currentStepData.component;

  const getResponsiveStyles = () => {
    return {
      heroSection: {
        height: isMobile ? "90vh" : "100vh",
        minHeight: isMobile ? "500px" : "600px",
      },
      heroContent: {
        padding: isSmallMobile ? "0 15px" : isMobile ? "0 20px" : "0 5%",
        textAlign: isMobile ? "center" : "left",
        marginLeft: isMobile ? "auto" : "5%",
        marginRight: isMobile ? "auto" : "auto",
      },
      pageTitle: {
        fontSize: isSmallMobile ? "2rem" : isMobile ? "2.5rem" : "3.8rem",
        textAlign: isMobile ? "center" : "left",
        marginBottom: isMobile ? "15px" : "24px",
      },
      pageSubtitle: {
        fontSize: isSmallMobile ? "0.9rem" : isMobile ? "1rem" : "1.2rem",
        textAlign: isMobile ? "center" : "left",
        marginBottom: isMobile ? "30px" : "40px",
      },
      featureGrid: {
        display: isMobile ? "none" : "grid",
        gridTemplateColumns: isTablet ? "1fr 1fr" : "repeat(3, 1fr)",
        gap: isTablet ? "20px" : "30px",
        marginBottom: "40px",
      },
      startButton: {
        width: isMobile ? "100%" : "auto",
        height: isSmallMobile ? "48px" : "54px",
        fontSize: isSmallMobile ? "0.85rem" : "0.95rem",
      },
      progressContainer: {
        padding: isMobile ? "10px 0" : "20px 0",
      },
      stepContent: {
        padding: isSmallMobile ? "20px 15px" : isMobile ? "25px 20px" : "40px",
        margin: isMobile ? "20px auto 80px" : "30px auto 0",
      },
      stepTitle: {
        fontSize: isSmallMobile ? "20px" : isMobile ? "24px" : "28px",
      },
      stepSubtitle: {
        fontSize: isSmallMobile ? "14px" : isMobile ? "15px" : "17px",
      },
      stepsIndicator: {
        gap: isSmallMobile ? "10px" : isMobile ? "15px" : "25px",
      },
      stepIconContainer: {
        width: isSmallMobile ? "32px" : isMobile ? "36px" : "44px",
        height: isSmallMobile ? "32px" : isMobile ? "36px" : "44px",
      },
      contentTopLabel: {
        justifyContent: isMobile ? "center" : "flex-start",
      },
      schematicElements: {
        display: isMobile ? "none" : "block",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <PageContainer>
      <div style={styles.configContainer}>
        {/* Updated Hero section with light theme */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          style={styles.heroSection}
        >
          <div style={styles.heroBackground}>
            {/* Blueprint grid overlay */}
            <div style={styles.blueprintGrid}></div>

            {/* Animated elements */}

            {/* Product schematic elements */}
            <div style={styles.schematicElements}>
              <motion.div
                style={styles.elevatorSchematic}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 0.15, y: 0 }}
                transition={{ delay: 0.5, duration: 1.2 }}
              >
                {/* SVG elevator schematic could be added here */}
                <div style={styles.schematicLine1}></div>
                <div style={styles.schematicLine2}></div>
                <div style={styles.schematicLine3}></div>
                <div style={styles.schematicCircle}></div>
                <div style={styles.schematicRect}></div>
              </motion.div>
            </div>

            {/* 3D product visualization hint */}

            {/* Content area */}
            <div style={styles.heroContent}>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                style={{
                  ...styles.contentTopLabel,
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                <div style={styles.labelBar}></div>
                <span style={styles.labelText}>INTERACTIVE TOOL</span>
                <div style={styles.labelBar}></div>
              </motion.div>

              <motion.h1
                style={{
                  ...styles.pageTitle,
                  fontSize: isMobile ? "2.5rem" : "3.8rem",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                ELEVATOR <span style={styles.titleHighlight}>CONFIGURATOR</span>
              </motion.h1>

              <motion.p
                style={styles.pageSubtitle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Design your perfect elevator with precision engineering tools
                and real-time visualization
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                {!isMobile && (
                  <div style={styles.featureGrid}>
                    <div style={styles.featureItem}>
                      <div style={styles.featureIcon}>
                        <i className="fas fa-cube"></i>
                      </div>
                      <div style={styles.featureText}>
                        <div style={styles.featureTitle}>3D Preview</div>
                        <div style={styles.featureDesc}>
                          Real-time rendering
                        </div>
                      </div>
                    </div>

                    <div style={styles.featureItem}>
                      <div style={styles.featureIcon}>
                        <i className="fas fa-cogs"></i>
                      </div>
                      <div style={styles.featureText}>
                        <div style={styles.featureTitle}>
                          Precision Engineering
                        </div>
                        <div style={styles.featureDesc}>
                          Exact specifications
                        </div>
                      </div>
                    </div>

                    <div style={styles.featureItem}>
                      <div style={styles.featureIcon}>
                        <i className="fas fa-file-export"></i>
                      </div>
                      <div style={styles.featureText}>
                        <div style={styles.featureTitle}>Get A quote</div>
                        <div style={styles.featureDesc}>PDF available</div>
                      </div>
                    </div>
                  </div>
                )}

                <motion.button
                  style={styles.startButton}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 0 25px rgba(0, 157, 255, 0.6)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    window.scrollTo({
                      top: window.innerHeight,
                      behavior: "smooth",
                    })
                  }
                >
                  START CONFIGURING
                  <i
                    className="fas fa-arrow-down"
                    style={styles.buttonIcon}
                  ></i>
                </motion.button>
              </motion.div>
            </div>

            {/* Tech details floating elements */}
            {/* <div style={styles.techDetailsContainer}>
              <motion.div
                style={styles.techDetail1}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
              >
                <div style={styles.techLine}></div>
                <div style={styles.techDot}></div>
                <div style={styles.techText}>Shaft Dimensions</div>
              </motion.div>

              <motion.div
                style={styles.techDetail2}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.8 }}
              >
                <div style={styles.techLine}></div>
                <div style={styles.techDot}></div>
                <div style={styles.techText}>Interior Finish</div>
              </motion.div>

              <motion.div
                style={styles.techDetail3}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.8 }}
              >
                <div style={styles.techLine}></div>
                <div style={styles.techDot}></div>
                <div style={styles.techText}>Door Configuration</div>
              </motion.div>
            </div> */}
          </div>
        </motion.div>

        {/* Progress visualization with fixed positioning for mobile */}
        <div
          style={{
            ...styles.progressContainer,
            ...responsiveStyles.progressContainer,
          }}
        >
          <div style={styles.progressBarContainer}>
            <div style={{ ...styles.progressBar, width: `${progress}%` }}></div>
          </div>

          <div
            style={{
              ...styles.stepsIndicator,
              ...responsiveStyles.stepsIndicator,
            }}
          >
            {steps.map((step, index) => {
              // Special case for showing ellipsis on mobile
              if (
                isMobile &&
                index !== 0 &&
                index !== currentStep - 1 &&
                index !== currentStep &&
                index !== currentStep + 1 &&
                index !== steps.length - 1
              ) {
                return null;
              }

              // Show ellipsis when steps are hidden
              if (
                isMobile &&
                (index === currentStep - 1 || index === currentStep + 1) &&
                (index === 1 || index === steps.length - 2) &&
                steps.length > 4 &&
                currentStep !== 1 &&
                currentStep !== steps.length - 2
              ) {
                return (
                  <span key={`ellipsis-${index}`} style={styles.stepEllipsis}>
                    •••
                  </span>
                );
              }

              return (
                <div
                  key={index}
                  style={{
                    ...styles.stepIndicator,
                    ...(index < currentStep ? styles.completedStep : {}),
                    ...(index === currentStep ? styles.activeStep : {}),
                  }}
                  onClick={
                    index <= currentStep ? () => goToStep(index) : undefined
                  }
                >
                  <div
                    style={{
                      ...styles.stepIconContainer,
                      ...responsiveStyles.stepIconContainer,
                      ...(index < currentStep ? styles.completedStepIcon : {}),
                      ...(index === currentStep ? styles.activeStepIcon : {}),
                    }}
                  >
                    {index < currentStep ? (
                      <i className="fas fa-check" style={styles.stepIcon}></i>
                    ) : (
                      <i
                        className={`fas fa-${step.icon}`}
                        style={styles.stepIcon}
                      ></i>
                    )}
                  </div>

                  {(!isMobile || index === currentStep) && (
                    <span
                      style={{
                        ...styles.stepLabel,
                        ...(index === currentStep
                          ? styles.activeStepLabel
                          : {}),
                      }}
                    >
                      {isMobile && index !== currentStep ? "" : step.title}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current step content with improved animations */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            style={styles.stepContent}
          >
            <div style={styles.stepHeader}>
              <h2 style={styles.stepTitle}>{currentStepData.title}</h2>
              <p style={styles.stepSubtitle}>{currentStepData.subtitle}</p>
            </div>

            <StepComponent {...currentStepData.props} />

            {/* Bottom navigation with improved accessibility */}
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={styles.bottomNavigation}
              >
                <div style={styles.stepCounter}>
                  Step {currentStep + 1} of {steps.length - 1}
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Mobile action buttons - fixed at bottom for easy access */}
        {isMobile && currentStep > 0 && currentStep < steps.length - 1 && (
          <div style={styles.mobileActionButtons}>
            <button
              onClick={goToNextStep}
              style={styles.mobileContinueButton}
              aria-label="Continue to next step"
            >
              Continue
              <i
                className="fas fa-chevron-right"
                style={styles.buttonIconRight}
              ></i>
            </button>
          </div>
        )}

      </div>
    </PageContainer>
  );
};

const styles = {
  configContainer: {
    maxWidth: "100vw",
    margin: "0 auto",
    padding: "0 0 60px",
    fontFamily:
      '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    position: "relative",
    backgroundColor: "#0a0f1e",
  },
  heroSection: {
    width: "100%",
    marginBottom: "30px",
  },
  heroBackground: {
    position: "relative",
    height: "100vh",
    backgroundColor: "#0a1222",
    backgroundImage: "linear-gradient(180deg, #101c2e 0%, #0a1222 100%)",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "@media (max-width: 768px)": {
      height: "80vh",
    },
  },
  blueprintGrid: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(0, 157, 255, 0.07) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 157, 255, 0.07) 1px, transparent 1px)
    `,
    backgroundSize: "30px 30px",
    opacity: 0.7,
    zIndex: 1,
  },
  techCircle1: {
    position: "absolute",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(0, 157, 255, 0.15) 0%, rgba(10, 18, 34, 0) 70%)",
    top: "10%",
    right: "15%",
    zIndex: 1,
  },
  techCircle2: {
    position: "absolute",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(0, 157, 255, 0.1) 0%, rgba(10, 18, 34, 0) 70%)",
    bottom: "15%",
    left: "10%",
    zIndex: 1,
  },
  schematicElements: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "40%",
    zIndex: 1,
    overflow: "hidden",
  },
  elevatorSchematic: {
    position: "absolute",
    width: "500px",
    height: "500px",
    right: "-100px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  schematicLine1: {
    position: "absolute",
    width: "200px",
    height: "1px",
    backgroundColor: "rgba(0, 157, 255, 0.3)",
    top: "30%",
    right: "30%",
  },
  schematicLine2: {
    position: "absolute",
    width: "120px",
    height: "1px",
    backgroundColor: "rgba(0, 157, 255, 0.3)",
    top: "50%",
    right: "20%",
  },
  schematicLine3: {
    position: "absolute",
    width: "180px",
    height: "1px",
    backgroundColor: "rgba(0, 157, 255, 0.3)",
    top: "70%",
    right: "25%",
  },
  schematicCircle: {
    position: "absolute",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid rgba(0, 157, 255, 0.4)",
    top: "40%",
    right: "40%",
  },
  schematicRect: {
    position: "absolute",
    width: "80px",
    height: "120px",
    border: "1px solid rgba(0, 157, 255, 0.4)",
    top: "50%",
    right: "50%",
  },
  product3DContainer: {
    position: "absolute",
    width: "300px",
    height: "400px",
    top: "50%",
    right: "10%",
    transform: "translateY(-50%)",
    zIndex: 2,
  },
  product3DHighlight: {
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "10px",
    background:
      "radial-gradient(circle, rgba(0, 157, 255, 0.1) 0%, rgba(10, 18, 34, 0) 70%)",
    filter: "blur(10px)",
  },
  product3DModel: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modelElevator: {
    width: "150px",
    height: "300px",
    position: "relative",
    border: "1px solid rgba(0, 157, 255, 0.6)",
    borderRadius: "5px",
    boxShadow: "0 0 20px rgba(0, 157, 255, 0.3)",
  },
  modelShaft: {
    position: "absolute",
    top: "5%",
    left: "10%",
    width: "80%",
    height: "90%",
    border: "1px dashed rgba(255, 255, 255, 0.2)",
  },
  modelCar: {
    position: "absolute",
    top: "30%",
    left: "20%",
    width: "60%",
    height: "30%",
    background: "rgba(0, 157, 255, 0.15)",
    border: "1px solid rgba(0, 157, 255, 0.6)",
  },
  modelDoor: {
    position: "absolute",
    top: "30%",
    left: "20%",
    width: "60%",
    height: "30%",
    borderRight: "1px solid rgba(0, 157, 255, 0.8)",
    borderLeft: "1px solid rgba(0, 157, 255, 0.8)",
  },
  heroContent: {
    position: "relative",
    zIndex: 3,
    padding: "0 20px",
    maxWidth: "700px",
    marginRight: "auto",
    marginLeft: "5%",
    color: "#ffffff",
    "@media (max-width: 1200px)": {
      marginLeft: 0,
      marginRight: 0,
      textAlign: "center",
    },
    "@media (max-width: 720px)": {
      marginLeft: 0,
      marginRight: 0,
      textAlign: "center",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  contentTopLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: "25px",
    "@media (max-width: 1200px)": {
      justifyContent: "center",
    },
  },
  labelBar: {
    width: "30px",
    height: "1px",
    backgroundColor: "rgba(0, 157, 255, 0.8)",
  },
  labelText: {
    margin: "0 12px",
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "rgba(0, 157, 255, 0.8)",
    letterSpacing: "2px",
    textTransform: "uppercase",
  },
  pageTitle: {
    fontSize: "3.8rem",
    fontWeight: 800,
    marginBottom: "24px",
    color: "#fff",
    letterSpacing: "1px",
    lineHeight: 1.2,
    textShadow: "0 0 20px rgba(0, 0, 0, 0.2)",
    "@media (max-width: 768px)": {
      fontSize: "2.5rem",
    },
  },
  titleHighlight: {
    color: "#00A3FF",
    textShadow: "0 0 15px rgba(0, 157, 255, 0.5)",
  },
  pageSubtitle: {
    fontSize: "1.2rem",
    fontWeight: 300,
    margin: "0 auto 30px",
    color: "rgba(255, 255, 255, 0.8)",
    marginBottom: "40px",
    lineHeight: 1.6,
    maxWidth: "600px",
    "@media (max-width: 768px)": {
      fontSize: "1rem",
    },
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 20px",
    maxWidth: "100vw",
    gap: "100px",
    marginLeft: "50px",
    marginBottom: "40px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      gap: "15px",
      opacity: 0,
    },
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
  },
  featureIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "rgba(0, 157, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "15px",
    fontSize: "1.2rem",
    color: "#00A3FF",
    border: "1px solid rgba(0, 157, 255, 0.3)",
  },
  featureText: {
    display: "flex",
    flexDirection: "column",
  },
  featureTitle: {
    fontSize: "1rem",
    fontWeight: 500,
    color: "#ffffff",
    marginBottom: "3px",
  },
  featureDesc: {
    fontSize: "0.85rem",
    color: "rgba(255, 255, 255, 0.6)",
  },
  startButton: {
    alignItems: "center",
    justifyContent: "center",
    padding: "0 30px",
    height: "54px",
    backgroundColor: "#00A3FF",
    color: "#ffffff",
    fontSize: "0.95rem",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    letterSpacing: "1px",
    boxShadow: "0 0 20px rgba(0, 157, 255, 0.4)",
    "@media (max-width: 768px)": {
      width: "100%",
    },
  },
  buttonIcon: {
    marginLeft: "10px",
    fontSize: "0.9rem",
  },
  techDetailsContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    pointerEvents: "none",
  },
  techDetail1: {
    position: "absolute",
    left: "60%",
    top: "30%",
    display: "flex",
    alignItems: "center",
    "@media (max-width: 1200px)": {
      display: "none",
    },
  },
  techDetail2: {
    position: "absolute",
    right: "15%",
    top: "40%",
    display: "flex",
    alignItems: "center",
    "@media (max-width: 1200px)": {
      display: "none",
    },
  },
  techDetail3: {
    position: "absolute",
    right: "25%",
    bottom: "35%",
    display: "flex",
    alignItems: "center",
    "@media (max-width: 1200px)": {
      display: "none",
    },
  },
  techLine: {
    width: "40px",
    height: "1px",
    backgroundColor: "rgba(0, 157, 255, 0.6)",
  },
  techDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#00A3FF",
    margin: "0 5px",
  },
  techText: {
    fontSize: "0.8rem",
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: 300,
  },

  heroOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage:
      "radial-gradient(circle at 30% 40%, rgba(33, 150, 243, 0.05) 0%, rgba(255, 255, 255, 0) 70%)",
    zIndex: 1,
  },
  gridOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(33, 150, 243, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(33, 150, 243, 0.05) 1px, transparent 1px)
    `,
    backgroundSize: "40px 40px",
    opacity: 0.6,
    zIndex: 1,
  },
  floatingShape1: {
    position: "absolute",
    width: "300px",
    height: "300px",
    borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
    background:
      "linear-gradient(45deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0) 70%)",
    top: "15%",
    right: "10%",
    zIndex: 1,
  },
  floatingShape2: {
    position: "absolute",
    width: "250px",
    height: "250px",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    background:
      "linear-gradient(45deg, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)",
    bottom: "20%",
    left: "10%",
    zIndex: 1,
  },

  heroContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    padding: "0 20px",
    maxWidth: "1000px",
  },
  titleContainer: {
    position: "relative",
    display: "inline-block",
    margin: "0 auto",
  },
  titleBracket: {
    position: "absolute",
    top: "50%",
    left: 0,
    transform: "translate(-120%, -50%)",
    width: 60,
    height: 2,
    backgroundColor: "#0062cc",
    "&::before": {
      content: '""',
      position: "absolute",
      right: 0,
      height: 20,
      width: 2,
      backgroundColor: "#0062cc",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      height: 20,
      width: 2,
      backgroundColor: "#0062cc",
    },
  },

  heroStats: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  statNumber: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#0062cc",
    lineHeight: 1,
    fontFamily: "'Montserrat', sans-serif",
  },
  statLabel: {
    fontSize: "0.9rem",
    color: "#666666",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginTop: "5px",
  },
  statDivider: {
    width: "1px",
    height: "40px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  scrollPrompt: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 2,
  },
  scrollLine: {
    width: "1px",
    height: "60px",
    backgroundColor: "rgba(0, 98, 204, 0.2)",
    margin: "0 auto",
    position: "relative",
  },
  scrollCircle: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#0062cc",
    margin: "0 auto",
    marginTop: "-3px",
    boxShadow: "0 0 10px rgba(0, 98, 204, 0.3)",
  },
  progressContainer: {
    position: "sticky",
    top: "50px", // Increased to avoid navbar overlap
    backgroundColor: "transparent",
    padding: "20px 0",
    zIndex: 10,
    borderBottom: "1px solid #e6e6e6",
    backdropFilter: "blur(30px)",
    boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
    "@media (max-width: 768px)": {
      padding: "15px 0",
    },
  },
  progressBarContainer: {
    height: "5px",
    backgroundColor: "#f0f0f0",
    borderRadius: "3px",
    overflow: "hidden",
    margin: "0 auto 25px",
    maxWidth: "90%",
    "@media (max-width: 768px)": {
      height: "4px",
      margin: "0 auto 20px",
    },
  },
  progressBar: {
    height: "100%",
    background: "linear-gradient(90deg, #163ba2, #1e4fc9)",
    borderRadius: "3px",
    transition: "width 0.5s ease-in-out",
  },
  stepsIndicator: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "25px",
    padding: "0 20px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "@media (max-width: 768px)": {
      gap: "15px",
      justifyContent: "center",
    },
  },
  stepIndicator: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#86868b",
    cursor: "pointer",
    transition: "all 0.3s ease",
    padding: "5px",
    "@media (max-width: 768px)": {
      padding: "3px",
    },
  },
  stepIconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: "#f5f5f7",
    marginBottom: "10px",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    "@media (max-width: 768px)": {
      width: "36px",
      height: "36px",
      marginBottom: "5px",
    },
  },
  stepIcon: {
    fontSize: "18px",
    transition: "all 0.3s ease",
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
  },
  completedStep: {
    color: "#163ba2",
  },
  completedStepIcon: {
    backgroundColor: "#e6efff",
    color: "#163ba2",
    border: "1px solid #163ba2",
  },
  activeStep: {
    color: "#163ba2",
  },
  activeStepIcon: {
    backgroundColor: "#163ba2",
    color: "white",
    boxShadow: "0 3px 10px rgba(22, 59, 162, 0.3)",
    transform: "scale(1.1)",
  },
  stepLabel: {
    fontSize: "14px",
    fontWeight: "500",
    textAlign: "center",
    transition: "all 0.3s ease",
    "@media (max-width: 768px)": {
      fontSize: "12px",
      maxWidth: "80px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  activeStepLabel: {
    fontWeight: "600",
    color: "#163ba2",
  },
  stepEllipsis: {
    fontSize: "20px",
    color: "#86868b",
    margin: "0 5px",
    alignSelf: "center",
  },
  stepContent: {
    position: "relative",
    margin: "30px auto 0",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    "@media (max-width: 768px)": {
      padding: "30px 20px",
      margin: "20px auto 80px", // Added bottom margin for mobile action buttons
      borderRadius: "15px",
    },
  },
  stepHeader: {
    marginBottom: "35px",
    textAlign: "center",
  },
  stepTitle: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#1d1d1f",
    marginBottom: "10px",
    letterSpacing: "-0.01em",
    "@media (max-width: 768px)": {
      fontSize: "24px",
    },
  },
  stepSubtitle: {
    fontSize: "17px",
    fontWeight: "400",
    color: "#4d4d4d",
    "@media (max-width: 768px)": {
      fontSize: "15px",
    },
  },
  bottomNavigation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "40px",
    borderTop: "1px solid #e6e6e6",
    paddingTop: "20px",
    "@media (max-width: 768px)": {
      marginTop: "30px",
      paddingTop: "15px",
    },
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    backgroundColor: "transparent",
    color: "#163ba2",
    border: "2px solid #163ba2",
    borderRadius: "10px",
    fontWeight: "600",
    fontSize: "15px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "@media (max-width: 768px)": {
      padding: "10px 16px",
      fontSize: "14px",
    },
  },
  buttonIcon: {
    marginRight: "10px",
  },
  buttonIconRight: {
    marginLeft: "10px",
  },
  stepCounter: {
    color: "#86868b",
    fontSize: "15px",
    fontWeight: "500",
    "@media (max-width: 768px)": {
      fontSize: "14px",
    },
  },
  mobileActionButtons: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    padding: "15px 20px",
    backgroundColor: "white",
    boxShadow: "0 -5px 15px rgba(0,0,0,0.08)",
    zIndex: 20,
    display: "flex",
    justifyContent: "center",
  },
  mobileContinueButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: "400px",
    padding: "16px",
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontWeight: "600",
    fontSize: "16px",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },
  floatingSummary: {
    position: "absolute",
    top: "550px", // Increased to avoid overlap with header
    right: "40px",
    width: "300px",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    border: "1px solid #e6e6e6",
    "@media (max-width: 1300px)": {
      display: "none",
    },
  },
  summaryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  summaryTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: 0,
  },
  resetButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#0066cc",
    fontSize: "14px",
    cursor: "pointer",
    padding: "5px",
    borderRadius: "5px",
    transition: "all 0.2s ease",
  },
  summaryContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    padding: "8px 0",
    borderBottom: "1px solid #f5f5f7",
  },
  summaryLabel: {
    color: "#86868b",
    fontWeight: "500",
  },
  summaryValue: {
    color: "#1d1d1f",
    fontWeight: "500",
    maxWidth: "150px",
    textAlign: "right",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  summaryFooter: {
    marginTop: "25px",
  },
  summaryContinueButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "12px",
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontWeight: "600",
    fontSize: "15px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },
};

export default Products;
